/**
 * ClientLayout — Layout persistant pour toutes les pages client
 *
 * Affiche la sidebar gauche (données client, charts, groupes)
 * et le contenu de la page active dans la zone droite via Outlet.
 */

import { Outlet, Link, useParams, useNavigate } from 'react-router-dom';
import { colors } from '../../styles/tokens.js';
import { ClientProvider, useClient } from '../../contexts/ClientContext.js';
import { ClientSidebar } from './ClientSidebar.js';

function ClientLayoutInner() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { client, isLoading, error } = useClient();

  if (isLoading) {
    return (
      <div style={{ maxWidth: 1200, width: '100%' }}>
        <div style={{ textAlign: 'center', padding: '48px 0', color: colors.textMuted, fontSize: 14 }}>
          Chargement du client...
        </div>
      </div>
    );
  }

  if (error || !client) {
    return (
      <div style={{ maxWidth: 1200, width: '100%' }}>
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
          padding: '10px 14px', fontSize: 14, color: '#991b1b',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <i className="bi bi-exclamation-circle" />
          {error || 'Client introuvable'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, width: '100%' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, fontSize: 13, color: colors.textMuted }}>
        <Link to="/clients" style={{ color: colors.textMuted, textDecoration: 'none' }}>
          Clients
        </Link>
        <i className="bi bi-chevron-right" style={{ fontSize: 10 }} />
        <Link to={`/clients/${id}`} style={{ color: colors.accent, textDecoration: 'none' }}>
          {client.prenom} {client.nom}
        </Link>
      </div>

      {/* Header avec avatar + nom + boutons */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem',
      }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: 50, height: 50, background: colors.accent, borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, color: colors.text,
          }}>
            {client.prenom[0]}{client.nom[0]}
          </div>
          {client.prenom} {client.nom}
        </h1>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Link
            to={`/clients/${id}/maintenant`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6, border: '1px solid #7c3aed',
              background: '#7c3aed', color: '#fff', fontSize: 13,
              fontWeight: 500, textDecoration: 'none', fontFamily: 'inherit',
            }}
          >
            <i className="bi bi-clock" /> Maintenant
          </Link>
          <Link
            to={`/clients/${id}/timeline`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6, border: '1px solid #d946ef',
              background: 'transparent', color: '#d946ef', fontSize: 13,
              fontWeight: 500, textDecoration: 'none', fontFamily: 'inherit',
            }}
          >
            <i className="bi bi-calendar3" /> Timeline
          </Link>
          <button
            onClick={() => navigate(`/clients/${id}/analyse-ia`, { state: { reset: Date.now() } })}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6, border: '1px solid #7c3aed',
              background: 'transparent', color: '#7c3aed', fontSize: 13,
              fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            <i className="bi bi-robot" /> Analyse IA
          </button>
          <Link
            to={`/clients/${id}/import`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6, border: '1px solid #16a34a',
              background: 'transparent', color: '#16a34a', fontSize: 13,
              fontWeight: 500, textDecoration: 'none', fontFamily: 'inherit',
            }}
          >
            <i className="bi bi-cloud-arrow-up" /> Importer
          </Link>
          <Link
            to={`/clients/${id}/modifier`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '8px 14px', borderRadius: 6, border: `1px solid ${colors.border}`,
              background: 'transparent', color: colors.textMid, fontSize: 13,
              fontWeight: 500, textDecoration: 'none', fontFamily: 'inherit',
            }}
          >
            <i className="bi bi-pencil" /> Modifier
          </Link>
        </div>
      </div>

      {/* Layout 2 colonnes */}
      <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '1.5rem' }}>
        <ClientSidebar />
        <div>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

export function ClientLayout() {
  return (
    <ClientProvider>
      <ClientLayoutInner />
    </ClientProvider>
  );
}
